/**
 * AnalyticsCharts — the Recharts panel kit for the flagship Analytics section. Each visual mirrors the
 * Overview MetricsCharts posture VERBATIM: always wrapped in a <ResponsiveContainer dir="ltr"> (the chart
 * never mirrors, the page still RTLs), token-colored (no raw hex, L-2), rendering its OWN empty state when
 * the data is all-zero, and carrying an aria-label summary on the container (the SVG is decorative — the
 * figures also live in the tables/tooltips). Numerics stay LTR.
 *
 *   • TrendChart          — the reusable multi-series area/line trend (settled-cash, support-by-type).
 *   • RankedBarChart      — a horizontal ranked bar (top modules / analyzers / analysis-types / wilayas).
 *   • AnalyticsDonut      — a labelled donut (free-vs-paid, demand bands, run-rate by tier).
 *   • RankedTable         — a token-styled ranked <Table> with a proportional in-cell share bar.
 *
 * a11y: every chart container is role="img" + aria-label; the ranked table is a real <table> (scope="col").
 * AA contrast comes from the semantic tokens; the empty states are role="status".
 */
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useT } from "@/app";
import { token } from "@/design/theme";
import { Table, type Column } from "@/ui";
import type { ChartDatum, MergedSeriesPoint } from "../analytics.lib";
import { allZero, fmtCount, shortBucket } from "../analytics.lib";

const CHART_HEIGHT = 260;

const TOOLTIP_STYLE = {
  background: token.surface,
  border: `1px solid ${token.line}`,
  borderRadius: token.rSm,
  color: token.ink,
} as const;

/** Shared empty placeholder (role=status, glyph + text) — mirrors the Overview ChartEmpty. */
export function ChartEmpty({ glyph = "◷" }: { glyph?: string }) {
  const { t } = useT();
  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        alignItems: "center",
        justifyContent: "center",
        minHeight: CHART_HEIGHT,
        textAlign: "center",
        color: "var(--nv-slate)",
      }}
    >
      <span aria-hidden style={{ fontSize: "var(--nv-fs-h2)", color: "var(--nv-steel)" }}>
        {glyph}
      </span>
      <strong style={{ color: "var(--nv-ink)" }}>{t("mgmt.analytics.chart.empty.title")}</strong>
      <p style={{ margin: 0, maxWidth: "42ch", fontSize: "var(--nv-fs-caption)" }}>
        {t("mgmt.analytics.chart.empty.desc")}
      </p>
    </div>
  );
}

// ── Reusable multi-series trend (area or line) ──────────────────────────────────────────

export interface TrendSeriesConfig {
  /** The datum field this series reads. */
  dataKey: string;
  /** Human legend label. */
  label: string;
  /** Token color (var(--nv-*)). */
  color: string;
}

/**
 * TrendChart — the reusable multi-series trend. `area` renders soft-filled areas (settled-cash cash-flow
 * feel); otherwise crisp lines (support-by-type). Empty when every plotted value across every series is 0.
 * `valueFmt` formats both the axis and the tooltip so a money trend reads in DA and a count trend in counts.
 */
export function TrendChart({
  data,
  bucket,
  series,
  ariaLabel,
  area = false,
  valueFmt = fmtCount,
}: {
  data: ReadonlyArray<MergedSeriesPoint>;
  bucket: "day" | "month";
  series: ReadonlyArray<TrendSeriesConfig>;
  ariaLabel: string;
  area?: boolean;
  valueFmt?: (v: number) => string;
}) {
  const rows = data.map((p) => ({ ...p, label: shortBucket(p.date, bucket) }));
  const flat = rows.flatMap((r) => series.map((s) => Number((r as Record<string, number | string>)[s.dataKey] ?? 0)));
  if (rows.length === 0 || allZero(flat)) return <ChartEmpty glyph="◷" />;

  const Chart = area ? AreaChart : LineChart;

  return (
    <div style={{ width: "100%", height: CHART_HEIGHT }} dir="ltr" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={token.lineFaint} vertical={false} />
          <XAxis dataKey="label" stroke={token.slate} fontSize={12} tickLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} stroke={token.slate} fontSize={12} width={56} tickFormatter={(v: number) => valueFmt(Number(v))} />
          <Tooltip
            cursor={{ stroke: token.line }}
            formatter={(v: number | string, name: string) => [valueFmt(Number(v)), name]}
            contentStyle={TOOLTIP_STYLE}
          />
          {series.length > 1 && (
            <Legend
              verticalAlign="top"
              height={28}
              formatter={(value: string) => <span style={{ color: token.slate, fontSize: 12 }}>{value}</span>}
            />
          )}
          {series.map((s) =>
            area ? (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.18}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            ),
          )}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Horizontal ranked bars ──────────────────────────────────────────────────────────────

/**
 * RankedBarChart — a horizontal ranked bar (category axis on the Y so long labels read). Empty when the
 * total is 0. Each bar takes its own palette color. `valueFmt` formats the axis + tooltip.
 */
export function RankedBarChart({
  data,
  ariaLabel,
  valueFmt = fmtCount,
}: {
  data: ReadonlyArray<ChartDatum>;
  ariaLabel: string;
  valueFmt?: (v: number) => string;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <ChartEmpty glyph="▭" />;

  // Height grows with the row count so a 20-row ranking stays readable (min the default panel height).
  const height = Math.max(CHART_HEIGHT, data.length * 30 + 24);

  return (
    <div style={{ width: "100%", height }} dir="ltr" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data as ChartDatum[]} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid stroke={token.lineFaint} horizontal={false} />
          <XAxis type="number" allowDecimals={false} stroke={token.slate} fontSize={12} tickFormatter={(v: number) => valueFmt(Number(v))} />
          <YAxis
            type="category"
            dataKey="name"
            stroke={token.slate}
            fontSize={12}
            width={150}
            tickLine={false}
            interval={0}
          />
          <Tooltip
            cursor={{ fill: token.lineFaint }}
            formatter={(v: number | string) => [valueFmt(Number(v)), ""]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.key} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Labelled donut ──────────────────────────────────────────────────────────────────────

export function AnalyticsDonut({
  data,
  ariaLabel,
  valueFmt = fmtCount,
}: {
  data: ReadonlyArray<ChartDatum>;
  ariaLabel: string;
  valueFmt?: (v: number) => string;
}) {
  const { t } = useT();
  if (data.length === 0) return <ChartEmpty glyph="◔" />;

  return (
    <div style={{ width: "100%", height: CHART_HEIGHT }} dir="ltr" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data as ChartDatum[]}
            dataKey="value"
            nameKey="name"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.key} fill={d.color} stroke={token.surface} />
            ))}
          </Pie>
          <Tooltip formatter={(v: number | string, name: string) => [valueFmt(Number(v)), name]} contentStyle={TOOLTIP_STYLE} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value: string) => <span style={{ color: token.slate, fontSize: 12 }}>{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
      <span className="nv-sr-only">{t("mgmt.analytics.chart.donut.sr", { n: data.length })}</span>
    </div>
  );
}

// ── Ranked table (with a proportional in-cell share bar) ─────────────────────────────────

export interface RankedRow {
  key: string;
  /** Primary label (the module / analyzer / analysis-type name). */
  label: string;
  /** Optional secondary line (code / discipline / manufacturer). `undefined` = no sub line. */
  sub?: string | undefined;
  value: number;
  /** Optional 2nd metric (e.g. analyzer errors) — rendered as a muted trailing figure. */
  secondary?: { label: string; value: number; tone?: "neutral" | "danger" } | undefined;
}

/**
 * RankedTable — a token-styled ranked list as a real <table>. Each row shows its rank, label (+ optional
 * sub), a proportional share bar (width = value / max), and the formatted value. Degrades to the Table's
 * own EmptyState on no rows. `valueFmt` formats the value column.
 */
export function RankedTable({
  rows,
  metricHeader,
  valueFmt = fmtCount,
  emptyTitle,
  emptyDescription,
}: {
  rows: ReadonlyArray<RankedRow>;
  metricHeader: string;
  valueFmt?: (v: number) => string;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const { t } = useT();
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;

  const columns: ReadonlyArray<Column<RankedRow>> = [
    {
      key: "rank",
      header: "#",
      width: "3rem",
      align: "end",
      mono: true,
      cell: (_r, i) => <span style={{ color: "var(--nv-steel)" }}>{i + 1}</span>,
    },
    {
      key: "label",
      header: t("mgmt.analytics.table.item"),
      cell: (r) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ color: "var(--nv-ink)" }}>{r.label}</span>
          {r.sub && <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>{r.sub}</span>}
        </div>
      ),
    },
    {
      key: "share",
      header: t("mgmt.analytics.table.share"),
      width: "34%",
      cell: (r) => {
        const pct = Math.round((r.value / max) * 100);
        return (
          <div
            style={{ height: 10, borderRadius: "var(--nv-r-pill)", background: "var(--nv-surface-2)", overflow: "hidden" }}
            role="img"
            aria-label={`${r.label}: ${valueFmt(r.value)} (${pct} %)`}
          >
            <div
              style={{
                width: `${Math.max(pct, r.value > 0 ? 4 : 0)}%`,
                height: "100%",
                background: "var(--nv-azure)",
                transition: "width var(--nv-base) var(--nv-ease)",
              }}
            />
          </div>
        );
      },
    },
    {
      key: "value",
      header: metricHeader,
      align: "end",
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.value,
      cell: (r) => (
        <span className="nv-ltr-island" style={{ fontWeight: 600 }}>
          {valueFmt(r.value)}
          {r.secondary && (
            <span
              style={{
                marginInlineStart: "var(--nv-s-2)",
                fontWeight: 400,
                fontSize: "var(--nv-fs-caption)",
                color: r.secondary.tone === "danger" ? "var(--nv-danger)" : "var(--nv-steel)",
              }}
            >
              {r.secondary.label} {fmtCount(r.secondary.value)}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <Table<RankedRow>
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      caption={metricHeader}
      emptyTitle={emptyTitle ?? t("mgmt.analytics.chart.empty.title")}
      emptyDescription={emptyDescription ?? t("mgmt.analytics.chart.empty.desc")}
    />
  );
}
